import { useMemo } from "react";
import { ChevronRight } from "@repo/ui";
import type { Status, StatusLog, Workflow } from "~/types/api";

const formatDateTime = (dateString: string): string => {
  const date = new Date(`${dateString}Z`);

  const locale = navigator.language;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  return `${get("day")}.${get("month")}.${get("year")}   ${get("hour")}:${get("minute")}`;
};

export const ItemLogsTable = ({
  logs,
  workflows,
  statuses,
}: {
  logs: StatusLog[];
  workflows?: Workflow[];
  statuses?: Status[];
}) => {
  const namesByWorkflowId = useMemo(() => {
    if (!workflows) return {} as Record<number, string>;

    return Object.fromEntries(
      workflows.map((w) => [w.id, `${w.name} v. ${String(w.version).padStart(2, "0")}`])
    ) as Record<number, string>;
  }, [workflows]);

  if (logs.length === 0) {
    return (
      <tr>
        <td colSpan={6} className="py-8 text-center text-sm text-gray-500">
          No status logs found
        </td>
      </tr>
    );
  }

  return (
    <>
      {logs.map((log) => (
        <tr key={log.id} className="border-b border-gray-100">
          <td className="py-3 text-sm text-gray-700">{formatDateTime(log.createdUtc)}</td>
          <td className="py-3 text-sm text-gray-700">{log.username}</td>
          <td className="py-3 text-sm text-gray-700">
            {statuses?.find((s) => s.name === log.fromStatus)?.code
              ? `${statuses.find((s) => s.name === log.fromStatus)?.code} - `
              : ""}
            {log.fromStatus}
          </td>
          <td className="text-gxe-primary py-3 text-sm font-bold">
            <ChevronRight className="ml-1 inline-block size-4" />
          </td>
          <td className="py-3 text-sm text-gray-700">
            {statuses?.find((s) => s.name === log.toStatus)?.code
              ? `${statuses.find((s) => s.name === log.toStatus)?.code} - `
              : ""}
            {log.toStatus}
          </td>
          <td className="py-3 text-sm text-gray-700">
            {log.workflowId ? namesByWorkflowId[log.workflowId] : "—"}
          </td>
        </tr>
      ))}
    </>
  );
};
